import { useState, useRef, useEffect, lazy } from "react";
import {
	Settings as SettingsIcon,
	X,
	Users,
	LogOut,
	Clock,
	Calendar,
	Download,
	Shield,
	FileText,
	BarChart3,
	ChevronDown,
	Lock,
	Database
} from "lucide-react";
import { ModalSuspense } from "../common";
import { ColleagueSettings } from "./colleagues/ColleagueSettings";
import { useAuth } from "../../context/AuthContext";
import { ColleagueAvatar } from "./colleagues/ColleagueAvatar";
import { useUserSettings } from "./hooks/useUserSettings";
import { DisplaySettings } from "./DisplaySettings";

// Lazy load heavy admin and modal components
const UserManagement = lazy(() => import("../users/UserManagement").then(m => ({ default: m.UserManagement })));
const PeriodManagementModal = lazy(() => import("../admin/PeriodManagementModal").then(m => ({ default: m.PeriodManagementModal })));
const AvailabilityModal = lazy(() => import("./availability/AvailabilityModal").then(m => ({ default: m.AvailabilityModal })));
const CalendarReport = lazy(() => import("./availability/CalendarReport").then(m => ({ default: m.CalendarReport })));
const SubscriptionModal = lazy(() => import("./SubscriptionModal").then(m => ({ default: m.SubscriptionModal })));
const ExportModal = lazy(() => import("./ExportModal").then(m => ({ default: m.ExportModal })));
const DesiderataReportModal = lazy(() => import("./reports/DesiderataReportModal").then(m => ({ default: m.DesiderataReportModal })));
const AvailabilityReportModal = lazy(() => import("./reports/AvailabilityReportModal").then(m => ({ default: m.AvailabilityReportModal })));
const AggregatedAvailabilityReportModal = lazy(() => import("./reports/AggregatedAvailabilityReportModal").then(m => ({ default: m.AggregatedAvailabilityReportModal })));
const AggregatedLeaveReportModal = lazy(() => import("./reports/AggregatedLeaveReportModal").then(m => ({ default: m.AggregatedLeaveReportModal })));
const PasswordChangeModal = lazy(() => import("./PasswordChangeModal").then(m => ({ default: m.PasswordChangeModal })));
import { useTranslation } from "../../context/TranslationContext";

interface SettingsPanelProps {
	className?: string;
}

export function SettingsPanel({ }: SettingsPanelProps) {
	const { logout } = useAuth();
	const { t } = useTranslation();
	const { currentUser } = useUserSettings();
	const [isOpen, setIsOpen] = useState(false);
	const [showColleagueSettings, setShowColleagueSettings] = useState(false);
	const [showUserManagement, setShowUserManagement] = useState(false);
	const [showPeriodManagement, setShowPeriodManagement] = useState(false);
	const [showAvailability, setShowAvailability] = useState(false);
	const [showCalendarReport, setShowCalendarReport] = useState(false);
	const [showSubscription, setShowSubscription] = useState(false);
	const [showExport, setShowExport] = useState(false);
	const [showReportsMenu, setShowReportsMenu] = useState(false);
	const [showDesiderataReport, setShowDesiderataReport] = useState(false);
	const [showAvailabilityReport, setShowAvailabilityReport] = useState(false);
	const [showAggregatedAvailabilityReport, setShowAggregatedAvailabilityReport] = useState(false);
	const [showAggregatedLeaveReport, setShowAggregatedLeaveReport] = useState(false);
	const [showPasswordChange, setShowPasswordChange] = useState(false);
	const reportsMenuRef = useRef<HTMLDivElement>(null);

	const handleLogout = () => {
		logout();
		setIsOpen(false);
	};

	const handleOpenAvailability = () => {
		if (currentUser) {
			setShowAvailability(true);
			setIsOpen(false);
		}
	};

	const handleCloseAvailability = () => {
		setShowAvailability(false);
		setIsOpen(true);
	};

	const handleOpenCalendarReport = () => {
		if (currentUser) {
			setShowCalendarReport(true);
			setIsOpen(false);
		}
	};

	const handleCloseCalendarReport = () => {
		setShowCalendarReport(false);
		setIsOpen(true);
	};

	const handleOpenSubscription = () => {
		if (currentUser) {
			setShowSubscription(true);
			setIsOpen(false);
		}
	};

	const handleCloseSubscription = () => {
		setShowSubscription(false);
		setIsOpen(true);
	};

	const handleOpenExport = () => {
		if (currentUser) {
			setShowExport(true);
			setIsOpen(false);
		}
	};

	const handleCloseExport = () => {
		setShowExport(false);
		setIsOpen(true);
	};

	const handleOpenDesiderataReport = () => {
		setShowDesiderataReport(true);
		setShowReportsMenu(false);
		setIsOpen(false);
	};

	const handleCloseDesiderataReport = () => {
		setShowDesiderataReport(false);
		setIsOpen(true);
	};

	const handleOpenAvailabilityReport = () => {
		setShowAvailabilityReport(true);
		setShowReportsMenu(false);
		setIsOpen(false);
	};

	const handleCloseAvailabilityReport = () => {
		setShowAvailabilityReport(false);
		setIsOpen(true);
	};

	const handleOpenAggregatedAvailabilityReport = () => {
		setShowAggregatedAvailabilityReport(true);
		setShowReportsMenu(false);
		setIsOpen(false);
	};

	const handleCloseAggregatedAvailabilityReport = () => {
		setShowAggregatedAvailabilityReport(false);
		setIsOpen(true);
	};

	const handleOpenAggregatedLeaveReport = () => {
		setShowAggregatedLeaveReport(true);
		setShowReportsMenu(false);
		setIsOpen(false);
	};

	const handleCloseAggregatedLeaveReport = () => {
		setShowAggregatedLeaveReport(false);
		setIsOpen(true);
	};

	const handleOpenPasswordChange = () => {
		setShowPasswordChange(true);
		setIsOpen(false);
	};

	const handleClosePasswordChange = () => {
		setShowPasswordChange(false);
		setIsOpen(true);
	};

	const handleExportAllData = async () => {
		if (!currentUser) return;

		try {
			const response = await fetch('https://team-calendar.onrender.com/api/admin/export', {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${localStorage.getItem('token')}`,
				},
			});

			if (!response.ok) {
				throw new Error('Failed to export data');
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `data-export-${new Date().toISOString().split('T')[0]}.zip`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
		} catch (error) {
			console.error('Export failed:', error);
			alert('Failed to export data. Please try again.');
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (reportsMenuRef.current && !reportsMenuRef.current.contains(event.target as Node)) {
				setShowReportsMenu(false);
			}
		};

		if (showReportsMenu) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showReportsMenu]);

	const handleOpenPeriodManagement = () => {
		if (currentUser) {
			setShowPeriodManagement(true);
			setIsOpen(false);
		}
	};

	const handleClosePeriodManagement = () => {
		setShowPeriodManagement(false);
		setIsOpen(true);
	};

	const isAdmin = currentUser?.role === 'admin';

	return (
		<div data-tsx-id="settings-panel">
			<button
				onClick={() => setIsOpen(true)}
				className={`fixed bottom-1 right-1 bg-zinc-300 hover:bg-zinc-600 text-zinc-100 hover:text-zinc-300 rounded-full p-2 shadow-lg  z-10 transition-all duration-200`}
				aria-label={t('common.settings')}
				title={t('common.settings')}
			>
				<SettingsIcon className="w-6 h-6" />
			</button>

			{isOpen && (
				<div
					className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity"
					onClick={() => setIsOpen(false)}
				/>
			)}

			<div
				className={`fixed right-0 top-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
					} flex flex-col`}
			>
				<div className="flex items-center justify-between p-4 border-b">
					<h2 className="text-lg font-semibold text-zinc-900">
						{t('settings.settings')}
					</h2>
					<button
						onClick={() => setIsOpen(false)}
						className="p-2 text-zinc-500 hover:text-zinc-700 rounded-full transition-colors"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				<div className="flex-1 p-4 overflow-y-auto">
					<div className="space-y-6">
						<div>
							<div className="space-y-2">
								<button
									onClick={handleOpenAvailability}
									className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50"
								>
									<Clock className="w-4 h-4 mr-2" />
									{t('availability.setAvailability')}
								</button>
								<button
									onClick={handleOpenCalendarReport}
									className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50"
								>
									<FileText className="w-4 h-4 mr-2" />
									{t('availability.viewCalendarReport')}
								</button>
								<button
									onClick={handleOpenSubscription}
									className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50"
								>
									<Calendar className="w-4 h-4 mr-2" />
									{t('settings.subscribeToCalendar')}
								</button>
								<button
									onClick={handleOpenExport}
									className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50"
								>
									<Download className="w-4 h-4 mr-2" />
									{t('settings.exportEvents')}
								</button>
								<button
									onClick={handleOpenPasswordChange}
									className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50"
								>
									<Lock className="w-4 h-4 mr-2" />
									{t('auth.changePassword')}
								</button>
								<div className="relative" ref={reportsMenuRef}>
									<button
										onClick={() => setShowReportsMenu(!showReportsMenu)}
										className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50"
									>
										<div className="flex items-center">
											<BarChart3 className="w-4 h-4 mr-2" />
											{t('settings.reports')}
										</div>
										<ChevronDown className={`w-4 h-4 transition-transform ${showReportsMenu ? 'rotate-180' : ''}`} />
									</button>
									{showReportsMenu && (
										<div className="absolute left-0 right-0 mt-1 bg-white border border-zinc-300 rounded-md shadow-lg z-10">
											<button
												onClick={handleOpenDesiderataReport}
												className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 text-left"
											>
												<FileText className="w-4 h-4 mr-2" />
												{t('reports.desiderata')}
											</button>
											<button
												onClick={handleOpenAvailabilityReport}
												className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 text-left"
											>
												<FileText className="w-4 h-4 mr-2" />
												{t('reports.availability')}
											</button>
											<button
												onClick={handleOpenAggregatedAvailabilityReport}
												className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 text-left"
											>
												<FileText className="w-4 h-4 mr-2" />
												{t('reports.aggregatedAvailability')}
											</button>
											<button
												onClick={handleOpenAggregatedLeaveReport}
												className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 text-left"
											>
												<FileText className="w-4 h-4 mr-2" />
												{t('reports.aggregatedLeave')}
											</button>
										</div>
									)}
								</div>
							</div>
						</div>

						{isAdmin && (
							<div className="space-y-2">
								<h3 className="text-sm font-medium text-zinc-900 mb-2">
									{t('settings.admin')}
								</h3>
								<button
									onClick={() => setShowUserManagement(true)}
									className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50"
								>
									<Users className="w-4 h-4 mr-2" />
									{t('settings.manageUsers')}
								</button>
								<button
									onClick={() =>
										setShowColleagueSettings(true)
									}
									className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50"
								>
									<Users className="w-4 h-4 mr-2" />
									{t('settings.manageColleagueDisplay')}
								</button>
								<button
									onClick={handleOpenPeriodManagement}
									className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50"
								>
									<Shield className="w-4 h-4 mr-2" />
									{t('settings.definePeriods')}
								</button>
								<button
									onClick={handleExportAllData}
									className="flex items-center w-full px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50"
								>
									<Database className="w-4 h-4 mr-2" />
									{t('settings.exportAllData')}
								</button>
							</div>
						)}

					<DisplaySettings />

					</div>
				</div>

				{currentUser && (
					<div className="mt-auto border-t p-4">
						<div className="flex items-center justify-between">
							<div className="flex items-center">
								<ColleagueAvatar
									firstName={currentUser.firstName}
									lastName={currentUser.lastName}
									color={
										currentUser.settings?.colleagues?.[
											currentUser.id
										]?.color || "#a50026"
									}
									abbreviation={
										currentUser.settings?.colleagues?.[
											currentUser.id
										]?.initials
									}
									size="sm"
								/>
								<div className="ml-3">
									<p className="text-sm font-medium text-zinc-900">
										{currentUser.firstName}{" "}
										{currentUser.lastName}
									</p>
									<p className="text-xs text-zinc-500">
										{currentUser.email}
									</p>
								</div>
							</div>
							<button
								onClick={handleLogout}
								className="p-2 text-zinc-500 hover:text-zinc-700 rounded-full transition-colors"
								aria-label={t('auth.signOut')}
							>
								<LogOut className="w-5 h-5" />
							</button>
						</div>
					</div>
				)}
			</div>

			{showColleagueSettings && (
				<ColleagueSettings
					onClose={() => setShowColleagueSettings(false)}
				/>
			)}

			{showUserManagement && (
				<ModalSuspense>
					<UserManagement onClose={() => setShowUserManagement(false)} />
				</ModalSuspense>
			)}

			{showPeriodManagement && (
				<ModalSuspense>
					<PeriodManagementModal
						onClose={handleClosePeriodManagement}
					/>
				</ModalSuspense>
			)}

			{showAvailability && currentUser && (
				<ModalSuspense>
					<AvailabilityModal
						colleague={currentUser}
						onClose={handleCloseAvailability}
					/>
				</ModalSuspense>
			)}

			{showCalendarReport && currentUser && (
				<ModalSuspense>
					<CalendarReport
						colleague={currentUser}
						year={new Date().getFullYear().toString()}
						onClose={handleCloseCalendarReport}
					/>
				</ModalSuspense>
			)}

			{showSubscription && currentUser && (
				<ModalSuspense>
					<SubscriptionModal
						userId={currentUser.id}
						site={currentUser.site}
						onClose={handleCloseSubscription}
					/>
				</ModalSuspense>
			)}

			{showExport && currentUser && (
				<ModalSuspense>
					<ExportModal
						userId={currentUser.id}
						site={currentUser.site}
						onClose={handleCloseExport}
					/>
				</ModalSuspense>
			)}

			{showDesiderataReport && (
				<ModalSuspense>
					<DesiderataReportModal
						onClose={handleCloseDesiderataReport}
					/>
				</ModalSuspense>
			)}

			{showPasswordChange && (
				<ModalSuspense>
					<PasswordChangeModal
						onClose={handleClosePasswordChange}
					/>
				</ModalSuspense>
			)}

			{showAvailabilityReport && (
				<ModalSuspense>
					<AvailabilityReportModal
						onClose={handleCloseAvailabilityReport}
					/>
				</ModalSuspense>
			)}

			{showAggregatedAvailabilityReport && (
				<ModalSuspense>
					<AggregatedAvailabilityReportModal
						onClose={handleCloseAggregatedAvailabilityReport}
					/>
				</ModalSuspense>
			)}

			{showAggregatedLeaveReport && (
				<ModalSuspense>
					<AggregatedLeaveReportModal
						onClose={handleCloseAggregatedLeaveReport}
					/>
				</ModalSuspense>
			)}
		</div>
	);
}